import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { BillViewModel } from '../../../../Annual-Details/Taxes/bill-cluster.service';
import { Bill } from '../../../../Annual-Details/Taxes/bill.model';
import { WeissmanModalService } from '../../../../Compliance/WeissmanModalService';
import { IdpBillDetailModalComponent, IdpBillDetailModalParams } from './Detail-Modal/idpBillDetailModal.component';
import { cloneDeep, omit } from 'lodash';
import { MappingExplanations } from './idp.service';
import { AssessmentViewModel } from 'src/app/Annual-Details/Assessments/assessment.service';
import {
    IdpAssessmentDetailModalComponent,
    IdpAssessmentDetailModalParams,
} from './Detail-Modal/idpAssessmentDetailModal.component';
import { AssessmentComponent } from 'src/app/Annual-Details/Assessments/assessment.component';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { ProcessingService } from 'src/app/Processing/processing.service.upgrade';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'idp-confidence-details',
    templateUrl: './idpConfidenceDetails.component.html',
    styleUrls: ['./idpConfidenceDetails.component.scss'],
    standalone: false,
})
export class IdpConfidenceDetailsComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _weissmanModalService: WeissmanModalService,
        private readonly _processingService: ProcessingService,
    ) {}

    @Input() currentDocument: Core.IntakeItemInfo;
    @Input() billDetail: BillViewModel;
    @Input() assessmentDetail: AssessmentViewModel;
    @Input() assessmentChild: AssessmentComponent;
    @Input() disableApplyAll: boolean;
    @Input() mappingExplanations: MappingExplanations;
    @Input() isLowConfidence: boolean;

    @Output() idpAcceptedAssessmentDetail: EventEmitter<AssessmentViewModel> = new EventEmitter();
    @Output() idpAcceptedBill: EventEmitter<Bill[]> = new EventEmitter();

    @ViewChild('reasonPopover') reasonPopover: PopoverDirective;

    canSeeDetails = true;

    private _originalBillDetail: Bill[];
    private _originalAssessmentDetail: AssessmentViewModel;
    private _destroy$: Subject<void> = new Subject();

    get confidenceLevel(): 'Low' | 'High' {
        if (this.isLowConfidence) {
            return 'Low';
        } else {
            return 'High';
        }
    }

    get shouldShowItalicsLine(): boolean {
        return this.assessmentDetail && !(this.isLowConfidence && this.disableApplyAll);
    }

    ngOnInit(): void {
        this._processingService.changeProcessingDocument$.pipe(takeUntil(this._destroy$)).subscribe((x) => {
            this.canSeeDetails = true;
        });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    seeDetails(): void {
        if (this.billDetail) {
            this._seeBillDetails();
        } else if (this.assessmentDetail) {
            this._seeAssessmentDetails();
        }
    }

    reset(): void {
        if (this.billDetail) {
            this.idpAcceptedBill.emit(this._originalBillDetail);
        } else if (this.assessmentDetail) {
            this.idpAcceptedAssessmentDetail.emit(this._originalAssessmentDetail);
        }
        this.canSeeDetails = true;
    }

    private async _seeBillDetails(): Promise<void> {
        const params: IdpBillDetailModalParams = {
            confidence: this.confidenceLevel,
            idpResults: cloneDeep(this.currentDocument.taxBillOcrResults),
            billDetail: this.billDetail,
            disableApplyAll: this.disableApplyAll,
        };

        const modalResult = await this._weissmanModalService.showAsync(IdpBillDetailModalComponent, params, 'modal-lg');

        if (modalResult) {
            this._originalBillDetail = structuredClone(this.billDetail.model);
            this.canSeeDetails = false;
            this._updateIntakeItemExtraInfoJSON(modalResult.applyAll);
            this.idpAcceptedBill.emit(modalResult.bills);
        }
    }

    private async _seeAssessmentDetails(): Promise<void> {
        // Snapshot before the modal applies IDP values so Reset can restore the pre-IDP state
        const originalAssessmentDetail = cloneDeep(
            omit(this.assessmentDetail, ['assessmentService', 'decimalPipe']),
        ) as AssessmentViewModel; // structedClone doesn't work on angular services

        const params: IdpAssessmentDetailModalParams = {
            confidence: this.confidenceLevel,
            idpResults: cloneDeep(this.currentDocument.assessmentNoticeOcrResults),
            // The live view model, so values are applied to the same object the assessment component calculates on
            assessmentVM: this.assessmentDetail,
            disableApplyAll: this.assessmentDetail.isActual() || this.disableApplyAll,
            assessmentChild: this.assessmentChild,
            mappingExplanations: this.mappingExplanations,
        };

        const modalResult = await this._weissmanModalService.showAsync(
            IdpAssessmentDetailModalComponent,
            params,
            'modal-lg',
        );

        if (modalResult) {
            this._originalAssessmentDetail = originalAssessmentDetail;
            this.canSeeDetails = false;
            this._updateIntakeItemExtraInfoJSON(modalResult.applyAll);
            this.idpAcceptedAssessmentDetail.emit(modalResult.updatedAssessmentVM);
        }
    }

    private _updateIntakeItemExtraInfoJSON(applyAll: boolean): void {
        if (this.currentDocument.intakeItem.extraInfoJSON) {
            const obj = JSON.parse(this.currentDocument.intakeItem.extraInfoJSON);
            obj.idpValuesUsedApplyAll = applyAll;
            this.currentDocument.intakeItem.extraInfoJSON = JSON.stringify(obj);
        } else {
            this.currentDocument.intakeItem.extraInfoJSON = JSON.stringify({ idpValuesUsedApplyAll: true });
        }
    }
}
